import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Case, CasesResponse } from '@lnr-v2/types';
import { casesApiService } from '../services/cases.service';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Featured case with limited tags (max 2, sorted by priority)
 */
export interface FeaturedCase extends Omit<Case, 'tags'> {
  tags: string[];
}

/**
 * Return type for the useFeaturedCases hook
 */
export interface UseFeaturedCasesReturn {
  /** Array of featured cases with limited tags */
  cases: FeaturedCase[];
  /** Loading state */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Whether the query is currently refetching in the background */
  isRefetching: boolean;
  /** Function to manually trigger a refetch */
  refetch: () => void;
}

/**
 * Maximum number of tags to display per case
 */
const MAX_TAGS_PER_CASE = 2;

/**
 * Extract tag names from terms, sorted by sortOrder (priority)
 * Falls back to legacy tags array if terms is empty
 */
function extractTags(caseItem: Case, language: 'en' | 'fr' = 'en'): string[] {
  // If we have terms, use them (sorted by sortOrder for priority)
  if (caseItem.terms && caseItem.terms.length > 0) {
    return [...caseItem.terms]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .slice(0, MAX_TAGS_PER_CASE)
      .map((term) => (language === 'fr' ? term.nameFR : term.nameEN));
  }

  // Fallback to legacy tags
  return caseItem.tags.slice(0, MAX_TAGS_PER_CASE);
}

/**
 * Process cases to limit tags to MAX_TAGS_PER_CASE
 * Extracts tags from terms (sorted by priority) or falls back to legacy tags
 */
function processCaseTags(
  cases: Case[],
  language: 'en' | 'fr' = 'en'
): FeaturedCase[] {
  return cases.map((caseItem) => ({
    ...caseItem,
    tags: extractTags(caseItem, language),
  }));
}

/**
 * React Query hook for fetching featured cases with proper caching
 *
 * Features:
 * - Fetches only featured and published cases
 * - Limits tags to 2 per case (sorted by priority)
 * - Automatic caching with 5-minute stale time
 * - Request deduplication
 *
 * @returns Object containing featured cases data, loading state, and helper functions
 *
 * @example
 * ```tsx
 * const { cases, loading, error } = useFeaturedCases();
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 *
 * return cases.map(c => <CaseCard key={c.id} case={c} />);
 * ```
 */
export const useFeaturedCases = (): UseFeaturedCasesReturn => {
  const { language } = useLanguage();
  const lang = language as 'en' | 'fr';

  const query: UseQueryResult<CasesResponse, Error> = useQuery({
    queryKey: ['cases', 'featured', lang],
    queryFn: () =>
      casesApiService.getCases({
        isFeatured: true,
        language: lang, // Pass language for localized content
        sortBy: 'displayOrder',
        sortOrder: 'ASC',
        limit: 10,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
  });

  // Process cases to limit tags (using terms sorted by priority)
  const rawCases = query.data?.data?.items ?? [];
  const cases = processCaseTags(rawCases, lang);
  const errorMessage = query.error?.message ?? null;

  return {
    cases,
    loading: query.isLoading,
    error: errorMessage,
    isRefetching: query.isRefetching,
    refetch: () => {
      void query.refetch();
    },
  };
};

export default useFeaturedCases;
